import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export const Slider = ({ children }: { children: React.ReactNode }) => {
  const timer = useRef<NodeJS.Timeout | null>(null);
  const [selected, setSelected] = useState(0);

  const slideCount = useMemo(() => React.Children.count(children), [children]);

  const changeSlide = useCallback(
    (step: 1 | -1) => {
      if (timer.current) {
        clearInterval(timer.current);
        timer.current = setInterval(() => changeSlide(1), 5000);
      }

      const newSelected = selected + step;
      if (newSelected < 0) {
        setSelected(slideCount - 1);
        return;
      } else if (newSelected >= slideCount) {
        setSelected(0);
        return;
      }

      setSelected(newSelected);
    },
    [selected, slideCount],
  );

  useEffect(() => {
    timer.current = setInterval(() => changeSlide(1), 5000);

    return () => {
      if (timer.current) {
        clearInterval(timer.current);
      }
    };
  }, [changeSlide]);

  return (
    <div className="relative h-full">
      {React.Children.map(children, (child, i) => (
        <div
          key={i}
          className={cn(
            "absolute h-full w-full text-[#D1D1D6] transition-all duration-300 ease-in-out",
            {
              "invisible -left-10 opacity-0": i < selected,
              "visible left-0 opacity-100": i === selected,
              "invisible left-10 opacity-0": i > selected,
            },
          )}
        >
          {child}
        </div>
      ))}
      <div className="absolute bottom-0 left-0 flex w-full items-center gap-2">
        <button
          className="me-0.5 flex items-center justify-center rounded-full bg-transparent p-1 hover:bg-secondary/40 focus:outline-none"
          onClick={() => changeSlide(-1)}
        >
          <ChevronLeftIcon className="size-6 text-white" />
        </button>
        {React.Children.map(children, (_, i) => (
          <button
            key={i}
            className="group flex items-center justify-center rounded-full bg-transparent p-1 focus:outline-none"
            onClick={() => setSelected(i)}
          >
            <span
              className={cn(
                "rounded-full transition-all duration-300 ease-in-out",
                {
                  "size-3 bg-[#FAFAFA]/20 group-hover:bg-[#FAFAFA]":
                    i !== selected,
                  "h-3 w-12 bg-[#FAFAFA]/100 group-hover:bg-[#FAFAFA]":
                    i === selected,
                },
              )}
            />
          </button>
        ))}
        <button
          className="ms-0.5 flex items-center justify-center rounded-full bg-transparent p-1 hover:bg-secondary/40 focus:outline-none"
          onClick={() => changeSlide(1)}
        >
          <ChevronRightIcon className="size-6 text-white" />
        </button>
      </div>
    </div>
  );
};
